<script>
/**
 * @component ModalPracticeCatalog
 * @description Modal para crear o editar una prestación del maestro.
 *              Soporta todos los campos: código, nombre, tipo, subtipo, categoría,
 *              descripción, cobertura PMO y cargo al usuario.
 * @example <ModalPracticeCatalog :practice="selectedPractice" @close="close" @saved="onSaved" />
 */
import practiceCatalogService from "@/services/practice-catalog";

const TYPE_OPTIONS = [
  { value: "practice", label: "Práctica" },
  { value: "treatment", label: "Tratamiento" },
  { value: "medication", label: "Medicamento" },
  { value: "supply", label: "Insumo" },
  { value: "prosthesis", label: "Prótesis" },
  { value: "other", label: "Otro" },
];

const CATEGORY_OPTIONS = [
  { value: "consultation", label: "Consulta" },
  { value: "lab", label: "Laboratorio" },
  { value: "imaging", label: "Imagen" },
  { value: "surgery", label: "Cirugía" },
  { value: "dental", label: "Odontología" },
  { value: "mental_health", label: "Salud mental" },
  { value: "rehabilitation", label: "Rehabilitación" },
  { value: "emergency", label: "Emergencia" },
  { value: "hospitalization", label: "Internación" },
  { value: "maternity", label: "Maternidad" },
  { value: "medication", label: "Medicamento" },
  { value: "supply", label: "Insumo" },
  { value: "prosthesis", label: "Prótesis" },
  { value: "other", label: "Otro" },
];

export default {
  name: "ModalPracticeCatalog",

  props: {
    practice: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      form: {
        code: "",
        name: "",
        description: "",
        type: "practice",
        subtype: "",
        category: "consultation",
        pmo: {
          included: "no",
          maxPerYear: "",
          requiresAuthorization: false,
          note: "",
          source: "",
        },
        userCharge: {
          applies: false,
          type: "none",
          note: "",
          source: "",
        },
      },
      isSubmitting: false,
      typeOptions: TYPE_OPTIONS,
      categoryOptions: CATEGORY_OPTIONS,
    };
  },

  computed: {
    /**
     * @description Indica si el modal está en modo edición
     * @returns {boolean}
     */
    isEditing() {
      return !!(this.practice && this.practice._id);
    },

    /**
     * @description Título dinámico del modal
     * @returns {string}
     */
    modalTitle() {
      return this.isEditing ? "Editar Prestación" : "Nueva Prestación";
    },
  },

  created() {
    if (this.isEditing) {
      this.form = {
        code: this.practice.code || "",
        name: this.practice.name || "",
        description: this.practice.description || "",
        type: this.practice.type || "practice",
        subtype: this.practice.subtype || "",
        category: this.practice.category || "consultation",
        pmo: {
          included: this.practice.pmo?.included || "no",
          maxPerYear: this.practice.pmo?.maxPerYear || "",
          requiresAuthorization:
            this.practice.pmo?.requiresAuthorization || false,
          note: this.practice.pmo?.note || "",
          source: this.practice.pmo?.source || "",
        },
        userCharge: {
          applies: this.practice.userCharge?.applies || false,
          type: this.practice.userCharge?.type || "none",
          note: this.practice.userCharge?.note || "",
          source: this.practice.userCharge?.source || "",
        },
      };
    }
  },

  methods: {
    /**
     * @description Cierra el modal
     */
    close() {
      this.$emit("close");
    },

    /**
     * @description Valida y envía el formulario (crear o actualizar)
     * @returns {Promise<void>}
     */
    async submit() {
      if (!this.form.code || !this.form.name) {
        this.$message.warning("El código y nombre son obligatorios");
        return;
      }

      this.isSubmitting = true;

      try {
        if (this.isEditing) {
          await practiceCatalogService.update(this.practice._id, this.form);
          this.$message.success("Prestación actualizada");
        } else {
          await practiceCatalogService.create(this.form);
          this.$message.success("Prestación creada");
        }
        this.$emit("saved");
      } catch (error) {
        const msg =
          error.response?.data?.error || "Error al guardar la prestación";
        this.$message.error(msg);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template lang="pug">
  el-dialog(:title="modalTitle" :visible="true" @close="close" width="700px")
    el-form(:model="form" label-position="top" size="small")

      //- Card 1: Identificación
      .form-card
        .form-card__title Identificación
        el-row(:gutter="12")
          el-col(:span="6")
            el-form-item(label="Código *")
              el-input(v-model="form.code" placeholder="Ej: 100101")
          el-col(:span="18")
            el-form-item(label="Nombre *")
              el-input(v-model="form.name")
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label="Tipo")
              el-select(v-model="form.type" style="width:100%")
                el-option(v-for="opt in typeOptions" :key="opt.value" :label="opt.label" :value="opt.value")
          el-col(:span="8")
            el-form-item(label="Subtipo")
              el-input(v-model="form.subtype" placeholder="Ej: Práctica (general)")
          el-col(:span="8")
            el-form-item(label="Categoría")
              el-select(v-model="form.category" style="width:100%")
                el-option(v-for="opt in categoryOptions" :key="opt.value" :label="opt.label" :value="opt.value")
        el-form-item(label="Descripción")
          el-input(type="textarea" v-model="form.description" :rows="2")

      //- Card 2: Cobertura PMO
      .form-card
        .form-card__title Cobertura PMO
        el-row(:gutter="12")
          el-col(:span="12")
            el-form-item(label="Cobertura")
              el-select(v-model="form.pmo.included" style="width:100%")
                el-option(value="yes" label="Incluido")
                el-option(value="conditional" label="Condicionado")
                el-option(value="partial" label="Parcial")
                el-option(value="no" label="No incluido")
          el-col(:span="12")
            el-form-item(label="Tope anual")
              el-input(v-model="form.pmo.maxPerYear" placeholder="Ej: Sin tope anual específico")
        el-form-item
          el-checkbox(v-model="form.pmo.requiresAuthorization") Requiere autorización previa de la obra social / prepaga
        el-form-item(label="Condición / nota")
          el-input(type="textarea" v-model="form.pmo.note" :rows="2")
        el-form-item(label="Fuente normativa")
          el-input(v-model="form.pmo.source" placeholder="URL(s) separadas por '; '")
          .field-hint URLs separadas por "; "

      //- Card 3: Cargo al usuario
      .form-card
        .form-card__title Cargo al usuario
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="form.userCharge.applies") Aplica cargo
          el-col(:span="8")
            el-form-item(label="Tipo de cargo")
              el-select(v-model="form.userCharge.type" style="width:100%" :disabled="!form.userCharge.applies")
                el-option(value="copago" label="Copago")
                el-option(value="coseguro" label="Coseguro")
                el-option(value="arancel" label="Arancel")
                el-option(value="none" label="Sin cargo")
        el-form-item(label="Detalle del cargo")
          el-input(type="textarea" v-model="form.userCharge.note" :rows="2" :disabled="!form.userCharge.applies")
        el-form-item(label="Fuente normativa")
          el-input(v-model="form.userCharge.source" placeholder="URL(s) separadas por '; '" :disabled="!form.userCharge.applies")

    template(#footer)
      el-button(@click="close") Cancelar
      el-button(type="primary" @click="submit" :loading="isSubmitting") {{ isEditing ? 'Actualizar' : 'Crear' }}
</template>

<style scoped>
.form-card {
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 14px;
}

.form-card__title {
  font-size: 13.5px;
  font-weight: 700;
  color: #0b1f3a;
  margin-bottom: 14px;
}

.field-hint {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
}
</style>
